// authApi.ts · v12.10.11.1 (#1462 InviteInfo carries the name / mobile already entered, for the set-up page) · 2026-10-09
// authApi.ts · v12.10.9.4 (#1342 F9 password rules) · 2026-10-07 · VoiceSpectrum AI
// v12.10.9.1: #1288 trustToken on sign-in / code sign-in, trust on 2-step verify, trust helpers (readTrust / saveTrust / clearTrust), logout can forget this browser.
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1255/#1256/#1257) · 2026-10-06
// authApi.ts - every anonymous sign-in call of CONTRACT v1.0 §1 (/api/auth/...), plus logout and the
// "password change required" change-password call that runs with a just-issued access token.
//
// Every sign-in step answers with a LoginResult; the sign-in flow (pages/login/SignInFlow.tsx) is a state machine driven
// by result.step. Error bodies are { error, message?, secondsLeft?, triesLeft?, retryInSeconds?, failed? } and arrive as
// ApiError (lib/api.ts) - authError() reads them. Platform admins still get the old LoginResponse fields from /auth/login
// (accessToken / mfaRequired / preMfaToken), so LoginResult carries them as optional legacy fields.
import { api, ApiError } from './api';

export type SecondMethod = 'sms' | 'totp' | 'backup';
export type SetupMethod = 'totp' | 'sms';
export type CodeChannel = 'sms' | 'email';

export interface LoginResult {
  step?: 'done' | 'profile' | 'second' | 'setup';
  accessToken?: string | null;
  pendingToken?: string;
  methods?: SecondMethod[];
  maskedMobile?: string;
  canSkip?: boolean;
  graceDaysLeft?: number;
  enforceOn?: string;
  setupMethods?: SetupMethod[];
  role?: string;
  mustChangePassword?: boolean | null;
  requiresWelcomeChooser?: boolean | null;
  // v12.10.9.1 (#1288): 2-step trust for this browser
  trustDays?: number | null;
  trustToken?: string | null;
  trustUntil?: string | null;
  // legacy LoginResponse (platform admins, older API)
  mfaRequired?: boolean;
  preMfaToken?: string | null;
}

export interface CodeSent { sent: boolean; masked?: string | null; maskedMobile?: string | null; resendInSeconds?: number }
export interface CodesResult { backupCodes: string[]; result: LoginResult | null }
export interface InviteInfo { orgName: string; groupNames: string[]; identifier: string; firstName?: string | null; lastName?: string | null; mobile?: string | null }
export interface PasswordRules { minLength: number; reuse: number; complexity?: boolean; charMix?: string; context?: (string | null)[] }   // v12.10.9.4 (#1342)

export interface AuthErrorBody {
  error?: string;
  message?: string;
  secondsLeft?: number;
  triesLeft?: number;
  retryInSeconds?: number;
  failed?: string[];
  reason?: string;
  idleMinutes?: number;
  minutes?: number;
}

/** HTTP status (0 = network / not an ApiError) and the parsed error body. */
export function authError(err: unknown): { status: number; body: AuthErrorBody } {
  if (err instanceof ApiError) {
    const body = err.body && typeof err.body === 'object' ? (err.body as AuthErrorBody) : {};
    return { status: err.status, body };
  }
  return { status: 0, body: {} };
}

const A = '/api/auth';

/** Bearer header for a token that is not (yet) the stored one. Content-Type is repeated because api.ts spreads options
 *  after its own headers, so a custom `headers` object replaces the default one. */
function bearer(token: string): RequestInit {
  return { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } };
}

function withSlug<T extends object>(body: T, tenantSlug: string | null): T & { tenantSlug?: string } {
  return tenantSlug ? { ...body, tenantSlug } : body;
}

// ── v12.10.9.1 (#1288): the browser's 2-step trust, one per practice (localStorage vs_t2_trust:<slug>) ─────────────
export interface TrustRecord { token: string; until: string }
function trustKey(slug: string | null): string { return `vs_t2_trust:${(slug ?? '').toLowerCase()}`; }
export function readTrust(slug: string | null): TrustRecord | null {
  try {
    const raw = localStorage.getItem(trustKey(slug));
    if (!raw) return null;
    const t = JSON.parse(raw) as TrustRecord;
    if (!t?.token || !t.until || Date.parse(t.until) <= Date.now()) { localStorage.removeItem(trustKey(slug)); return null; }
    return t;
  } catch { return null; }
}
export function saveTrust(slug: string | null, t: TrustRecord): void { try { localStorage.setItem(trustKey(slug), JSON.stringify(t)); } catch { /* blocked */ } }
export function clearTrust(slug: string | null): void { try { localStorage.removeItem(trustKey(slug)); } catch { /* blocked */ } }
function trustTokenFor(slug: string | null): string | undefined { return readTrust(slug)?.token; }
/** The signed-in person's practice slug, read from the stored token (for screens outside the auth context). */
export function signedInSlug(): string | null {
  try {
    const t = localStorage.getItem('vs_t2_token'); if (!t) return null;
    const o = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const v = o.vs_tenant_slug ?? o.tenant_slug;
    return typeof v === 'string' ? v : null;
  } catch { return null; }
}

export const authApi = {
  login: (email: string, password: string, keepSignedIn: boolean, tenantSlug: string | null) =>
    api.post<LoginResult>(`${A}/login`, withSlug({ email, password, keepSignedIn, trustToken: trustTokenFor(tenantSlug) }, tenantSlug)),
  /** Old platform-admin second step (LoginResponse.mfaRequired). */
  legacyMfaVerify: (preMfaToken: string, totpCode: string) =>
    api.post<{ accessToken: string }>(`${A}/mfa/verify`, { preMfaToken, totpCode }),

  codeLoginStart: (identifier: string, channel: CodeChannel, tenantSlug: string | null) =>
    api.post<CodeSent>(`${A}/code-login/start`, withSlug({ identifier, channel }, tenantSlug)),
  codeLoginVerify: (identifier: string, channel: CodeChannel, code: string, keepSignedIn: boolean, tenantSlug: string | null) =>
    api.post<LoginResult>(`${A}/code-login/verify`, withSlug({ identifier, channel, code, keepSignedIn, trustToken: trustTokenFor(tenantSlug) }, tenantSlug)),

  profile: (pendingToken: string, firstName: string, lastName: string, mobile: string | null) =>
    api.post<LoginResult>(`${A}/profile`, { pendingToken, firstName, lastName, ...(mobile ? { mobile } : {}) }),

  secondSend: (pendingToken: string) =>
    api.post<CodeSent>(`${A}/second/send`, { pendingToken }),
  secondVerify: (pendingToken: string, method: SecondMethod, code: string, trust = false) =>
    api.post<LoginResult>(`${A}/second/verify`, { pendingToken, method, code, trust }),

  totpStart: (pendingToken: string) =>
    api.post<{ secret: string; otpauthUri: string }>(`${A}/setup/totp/start`, { pendingToken }),
  totpConfirm: (pendingToken: string, code: string) =>
    api.post<CodesResult>(`${A}/setup/totp/confirm`, { pendingToken, code }),
  smsStart: (pendingToken: string, mobile: string) =>
    api.post<CodeSent>(`${A}/setup/sms/start`, { pendingToken, mobile }),
  smsConfirm: (pendingToken: string, code: string) =>
    api.post<CodesResult>(`${A}/setup/sms/confirm`, { pendingToken, code }),
  skipSetup: (pendingToken: string) =>
    api.post<LoginResult>(`${A}/setup/skip`, { pendingToken }),

  forgot: (identifier: string, tenantSlug: string | null) =>
    api.post<{ via: 'email' | 'sms' | null }>(`${A}/forgot`, withSlug({ identifier }, tenantSlug)),
  reset: (body: { token?: string; identifier?: string; code?: string; newPassword: string; tenantSlug?: string }) =>
    api.post<{ ok: boolean }>(`${A}/reset`, body),
  passwordRules: (tenantSlug: string | null) =>
    api.get<PasswordRules>(`${A}/password-rules${tenantSlug ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : ''}`),

  invite: (token: string) =>
    api.get<InviteInfo>(`${A}/invite/${encodeURIComponent(token)}`),
  acceptInvite: (body: { token: string; firstName: string; lastName: string; mobile?: string; password: string }) =>
    api.post<LoginResult>(`${A}/invite/accept`, body),

  /** L8: the person is signed in (access token issued) but must change the password before entering. */
  changePassword: (accessToken: string, oldPassword: string, newPassword: string) =>
    api.post<{ ok: boolean; signedOutOthers: number }>(`${A}/change-password`, { oldPassword, newPassword }, bearer(accessToken)),

  /** Explicit sign-out: revokes this session. Best effort - the caller clears local state regardless. */
  logout: (accessToken: string, forgetTrustToken?: string | null) =>
    api.post<void>(`${A}/logout`, forgetTrustToken ? { forgetTrustToken } : {}, bearer(accessToken)),
};

export default authApi;
